import React, { useEffect, useRef, useState } from 'react';
import AnimatedTransition from '@/components/AnimatedTransition';
import TeamManagement from '@/components/TeamManagement';
import DivisionSelector from '@/components/DivisionSelector';
import { Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useProjectContext } from '@/contexts/ProjectContext';

interface Division {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
  division_id: string;
  club_id: string;
  created_at: string;
  project_id: string;
  club_logo?: string;
}

const slugify = (text: string): string =>
  text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const TeamsPage: React.FC = () => {
  const { project } = useProjectContext(); // Get the full project object from context
  const projectSlug = project?.slug;
  const { isAuthenticated, isAdmin, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const captainTeam = isAuthenticated && !isAdmin ? user?.team : null;

  const [divisions, setDivisions] = useState<Division[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState<string | null>(null); 

  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const teamListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchProjectId = async () => {
      if (!projectSlug) return;

      console.log('📦 TeamsPage - Fetching projectId for projectSlug:', projectSlug);
      try {
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('id')
          .eq('slug', projectSlug)
          .single();

        if (projectError) {
          console.error('❌ TeamsPage - projectError:', projectError);
          return;
        }

        if (projectData?.id) {
          console.log('✅ TeamsPage - Project ID found:', projectData.id);
          setProjectId(projectData.id);
        } else {
          console.warn('⚠️ TeamsPage - No project found with slug:', projectSlug);
        }
      } catch (error) {
        console.error('❌ TeamsPage - Error fetching project ID:', error);
      }
    };

    fetchProjectId();
  }, [projectSlug]);

  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      console.log('📦 TeamsPage - Project ID:', projectId);
      console.log('📦 TeamsPage - Loading teams and divisions for projectId:', projectId);

      try {
        const [teamRes, divisionRes] = await Promise.all([
          supabase
            .from('teams')
            .select('id, name, division_id, club_id, created_at, project_id, club:club_id(logo_path)')
            .eq('project_id', projectId),
          supabase.from('divisions').select('*').eq('project_id', projectId),
        ]);

        console.log('📦 TeamsPage - Team query response:', teamRes);
        console.log('📦 TeamsPage - Division query response:', divisionRes);

        const { data: teamData, error: teamError } = teamRes;
        const { data: divisionData, error: divisionError } = divisionRes;

        console.log('📦 TeamsPage - Team data:', teamData);
        console.log('📦 TeamsPage - Division data:', divisionData);

        console.log('📦 TeamsPage - Team data:', teamData);
        console.log('📦 TeamsPage - Division data:', divisionData);

        if (teamError) {
          console.error('❌ TeamsPage - teamError:', teamError);
          return;
        }
        if (divisionError) {
          console.error('❌ TeamsPage - divisionError:', divisionError);
          return;
        }

        setTeams(
          (teamData ?? [])
            .map((team) => ({
              ...team,
              club_logo: (team.club as { logo_path?: string })?.logo_path ?? null,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setDivisions((divisionData ?? []).sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      } catch (error) {
        console.error('❌ TeamsPage - Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  useEffect(() => {
    if (!divisions.length || !teams.length) return;

    const divSlug = searchParams.get('division');
    const teamSlug = searchParams.get('team');

    if (divSlug) {
      const div = divisions.find(d => slugify(d.name) === divSlug);
      if (div) setSelectedDivision(div);
    }

    if (teamSlug) {
      const team = teams.find(t => slugify(t.name) === teamSlug);
      if (team) setSelectedTeam(team);
    }
  }, [divisions, teams, searchParams]);

  useEffect(() => {
    if (!divisions.length) return;

    if (captainTeam) {
      const myTeam = teams.find((t) => t.id === captainTeam);
      if (myTeam) {
        const foundDivision = divisions.find((d) => d.id === myTeam.division_id);
        if (foundDivision) {
          setSelectedDivision(foundDivision);
          return;
        }
      }
    }

    setSelectedDivision(divisions[0]);
  }, [divisions, teams, captainTeam]);

  useEffect(() => {
    const inDiv = teams.filter((t) => t.division_id === selectedDivision?.id);
    console.log('📦 TeamsPage - Selected Division:', selectedDivision);
    console.log('📦 TeamsPage - Teams in selected division:', inDiv);
    if (!inDiv.length) {
      setSelectedTeam(null);
    } else if (captainTeam && inDiv.some((t) => t.id === captainTeam)) {
      setSelectedTeam(inDiv.find((t) => t.id === captainTeam) || null);
    } else {
      setSelectedTeam(inDiv[0]);
    }
  }, [selectedDivision, teams, captainTeam]);

  const filteredTeams = teams.filter((t) => t.division_id === selectedDivision?.id) ?? [];
  console.log('📦 TeamsPage - Filtered teams for selected division:', filteredTeams);

  return (
    <AnimatedTransition>
      <DivisionSelector
        divisions={divisions}
        selectedDivision={selectedDivision}
        selectedTeam={selectedTeam?.id}
        onSelectDivision={(div) => {
          setSelectedDivision(div);
          setSelectedTeam(null);
          setSearchParams({
            division: slugify(div.name)
          });
        }}
        onSelectTeam={(teamId: string) => {
          const team = teams.find((t) => t.id === teamId);
          setSelectedTeam(team || null);
          if (selectedDivision && team) {
            setSearchParams({
              division: slugify(selectedDivision.name),
              team: slugify(team.name),
            });
          }
        }}
        teamOptions={filteredTeams}
        className="mx-2 md:mx-0"
      />

      <div ref={teamListRef} className="mt-4">
        {loading ? (
          <p className="text-muted-foreground px-2">Loading teams…</p>
        ) : (
          <>
            <TeamManagement
              isAdmin={isAdmin}
              selectedTeam={selectedTeam}
            />
            {!isAuthenticated && (
              <Card className="mx-2 md:mx-0 mt-4">
                <CardHeader className="bg-muted/50 pb-3">
                  <h2 className="text-xl font-medium">Team Management</h2>
                </CardHeader>
                <CardContent className="p-6">
                  <Alert className="mb-6">
                    <Lock className="h-4 w-4" />
                    <AlertDescription>
                      You’re currently viewing the teams in read-only mode. Log in as a team captain to make changes.
                    </AlertDescription>
                  </Alert>
                  <div className="flex justify-center">
                    <Button onClick={() => navigate('/login')}>Captain Login</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AnimatedTransition>
  );
};

export default TeamsPage;